/**
 * Activity Helper Functions
 *
 * Utility functions for working with Activity entities.
 * These helpers support scheduled/unscheduled activity management.
 */

import type { Activity } from '../types/activity';

/**
 * Check if an activity is scheduled
 * @param activity - Activity to check
 * @returns true if activity has a dayIndex assigned
 */
export function isScheduled(activity: Activity): boolean {
  return activity.dayIndex !== null;
}

/**
 * Check if an activity has a specific time
 * @param activity - Activity to check
 * @returns true if activity has a scheduledTime value
 */
export function hasSpecificTime(activity: Activity): boolean {
  return activity.scheduledTime !== null;
}

/**
 * Get display time for an activity
 * @param activity - Activity to get time from
 * @returns Time string for display (HH:mm format), or null if no time
 */
export function getDisplayTime(activity: Activity): string | null {
  return activity.scheduledTime;
}

/**
 * Get sorting key for activities within a day
 *
 * Sorting rules:
 * - Sort by orderInDay (from backend API) in ascending order
 * - Activities with null order go to the end
 *
 * @param activity - Activity to generate sort key for
 * @returns Sort key string (padded order number)
 *
 * @example
 * // Activity with order 1: "00001"
 * // Activity with order 10: "00010"
 * // Activity with null order: "99999"
 */
export function getDaySortKey(activity: Activity): string {
  if (!isScheduled(activity)) return '';

  // Use orderInDay from backend API for sorting
  // If order is null, put it at the end
  const orderKey = activity.order !== null
    ? activity.order.toString().padStart(5, '0')
    : '99999';

  return orderKey;
}
